import { AlertTriangle, CheckCircle2, DollarSign, Layers, Loader2, Lock, Plus, Repeat, RotateCcw, Save, Timer, Trash2, Unlock, Zap } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { applySubpackIntervalsToActive, fetchSubpackSettings, updateSubpackEconomics } from '@/api/admin';
import { fmtUsd } from '@/lib/money';

/**
 * Subpack Settings subpage — Patch v18.3 Global Economics Governor.
 *
 * Adds a top-of-page Global Economics card and turns every static tier
 * display field into an editable input. Tier rows can be added and
 * removed. All values persist to the SystemConfig singleton via one
 * atomic PUT /api/admin/subpacks/settings; every hot-path consumer
 * (cycle engine, subpack purchase, position purchase) reads the new
 * values live on the next tick (TTL cache = 3 seconds max staleness).
 *
 * Sum invariant (enforced client + server):
 *   cycler + sponsor + admin_fee == position_price × 3
 *
 * Backward compatibility preserved:
 *   - "Apply to Active" per-tier + global buttons still work as before
 *   - Legacy interval-map PUT contract still accepted server-side
 *   - Existing data-testid attributes maintained for automation stability
 */

// ---- Time conversion helpers (single source of truth) ----
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

const SEC_TO_MIN = (s) => {
  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n / SECONDS_PER_MINUTE);
};
const _MIN_TO_SEC = (m) => {
  const n = Number(m);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * SECONDS_PER_MINUTE);
};
const _HOURS_TO_MIN = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 60);
};
const HOURS_TO_SEC = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * SECONDS_PER_HOUR);
};

/** D-31: a dollar amount with at most 2 decimals. */
const CENTS_TEXT = /^\d+(\.\d{1,2})?$/;
/** Dollar text → integer cents (NaN when it is not a 2-decimal amount). */
const toCents = (v) => {
  const text = String(v ?? '').trim();
  if (!CENTS_TEXT.test(text)) return NaN;
  const [whole, frac = ''] = text.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
};
/**
 * D-24: a range is optional; when given it needs both ends and min ≤ max. An
 * ineligible tier's (hidden) range is not sent, so it never blocks a save.
 */
const rangeValid = (t) => {
  if (t.withdrawal_eligible === false) return true;
  const min = String(t.withdrawal_min_usd ?? '').trim();
  const max = String(t.withdrawal_max_usd ?? '').trim();
  if (!min && !max) return true;
  if (!min || !max) return false;
  const a = toCents(min);
  const b = toCents(max);
  return Number.isFinite(a) && Number.isFinite(b) && a > 0 && a <= b;
};

// UI bounds — mirror backend TIER_INTERVAL_SECONDS_MIN/MAX.
const MIN_INTERVAL_SECONDS = 1;
const MAX_INTERVAL_SECONDS = 2_592_000; // 30 days

const MIN_TIER_PRICE = 1;
const MAX_TIER_PRICE = 100_000;
const MIN_POSITIONS = 1;
const MAX_POSITIONS = 10_000;

// Position/payout amount bounds (dollars).
const MIN_AMOUNT = 0;
const MAX_AMOUNT = 10_000;

const TIER_BADGE_COLORS = {
  5: '#FFE08A',
  10: '#FFD45E',
  25: '#FFC20E',
  50: '#FFAD00',
  100: '#FF9500',
  250: '#FF7A00',
  500: '#FF5C00',
};
const DEFAULT_TIER_COLOR = '#FFC20E';

const fmtSecondsHuman = (s) => {
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n >= SECONDS_PER_HOUR && n % SECONDS_PER_HOUR === 0) {
    return `${n / SECONDS_PER_HOUR} h`;
  }
  if (n >= SECONDS_PER_MINUTE && n % SECONDS_PER_MINUTE === 0) {
    return `${n / SECONDS_PER_MINUTE} min`;
  }
  return `${n} s`;
};

// Small decimal comparison helper — string form, avoids float drift.
const toMoneyStr = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
};

// ---- Validation helpers ----------------------------------------------
const parseNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : NaN;
};
const inRange = (v, lo, hi) => {
  const n = parseNum(v);
  return Number.isFinite(n) && n >= lo && n <= hi;
};

const tierRowValid = (t) => {
  if (!inRange(t.tier_price_usd, MIN_TIER_PRICE, MAX_TIER_PRICE)) return false;
  // D-31: prices may carry cents ($4.96), never more than 2 decimals.
  if (!CENTS_TEXT.test(String(t.tier_price_usd).trim())) return false;
  if (!rangeValid(t)) return false;
  if (!inRange(t.total_positions, MIN_POSITIONS, MAX_POSITIONS)) return false;
  if (!Number.isInteger(parseNum(t.total_positions))) return false;
  if (!inRange(t.interval_seconds, MIN_INTERVAL_SECONDS, MAX_INTERVAL_SECONDS)) return false;
  if (!Number.isInteger(parseNum(t.interval_seconds))) return false;
  return true;
};

const hydrateFromServer = (d) => {
  if (!d?.tiers || !d?.global_economics) return null;
  const g = d.global_economics;
  return {
    global: {
      position_price_usd: toMoneyStr(g.position_price_usd),
      payout_cycler: toMoneyStr(g.payout_cycler),
      payout_sponsor: toMoneyStr(g.payout_sponsor),
      payout_admin_fee: toMoneyStr(g.payout_admin_fee),
    },
    tiers: d.tiers.map((t) => ({
      tier_price_usd: String(t.tier_price_usd),
      total_positions: String(t.total_positions),
      interval_seconds: String(Number.isFinite(t.interval_seconds) ? t.interval_seconds : HOURS_TO_SEC(t.release_interval_hours)),
      // Patch v18.4 — Per-tier withdrawal-eligibility. Server omits =>
      // treat as TRUE (legacy default preserves prior behavior).
      withdrawal_eligible: typeof t.withdrawal_eligible === 'boolean' ? t.withdrawal_eligible : true,
      // D-24/D-26: the per-request withdrawal range of an eligible tier ("" = none).
      withdrawal_min_usd: t.withdrawal_min_usd ?? '',
      withdrawal_max_usd: t.withdrawal_max_usd ?? '',
      active_pack_count: Number(t.active_pack_count) || 0,
      source: t.source,
      spec_default_seconds: Number.isFinite(t.spec_default_seconds) ? t.spec_default_seconds : HOURS_TO_SEC(t.spec_default_hours),
      // Keep original server-shown price so we can detect renamed tiers
      _orig_tier_price_usd: t.tier_price_usd,
    })),
  };
};

const SubpackSettingsSubpage = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [data, setData] = useState(null);
  // form.global = { position_price, cycler, sponsor, admin_fee }  (all strings)
  // form.tiers  = [{ tier_price_usd, total_positions, interval_seconds, active_pack_count, source, spec_default_seconds, spec_default_hours }, ...]
  const [form, setForm] = useState({ global: null, tiers: [] });
  const [saving, setSaving] = useState(false);
  const [applyingTier, setApplyingTier] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [flashTiers, setFlashTiers] = useState(new Set());
  const [flashGlobal, setFlashGlobal] = useState(false);
  const uid = useId();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchSubpackSettings();
        if (cancelled) return;
        if (!d?.tiers || !d?.global_economics) throw new Error('malformed');
        setData(d);
        const hydrated = hydrateFromServer(d);
        if (hydrated) setForm(hydrated);
      } catch {
        if (!cancelled) {
          setLoadError(true);
          toast.error('Failed to load Sub Pack settings');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = async () => {
    try {
      const d = await fetchSubpackSettings();
      if (!d?.tiers || !d?.global_economics) return;
      setData(d);
      const hydrated = hydrateFromServer(d);
      if (hydrated) setForm(hydrated);
    } catch {
      // non-fatal
    }
  };

  const globalValid = useMemo(() => {
    if (!form.global) return false;
    if (!inRange(form.global.position_price_usd, MIN_AMOUNT + 0.01, MAX_AMOUNT)) return false;
    if (!inRange(form.global.payout_cycler, MIN_AMOUNT, MAX_AMOUNT)) return false;
    if (!inRange(form.global.payout_sponsor, MIN_AMOUNT, MAX_AMOUNT)) return false;
    if (!inRange(form.global.payout_admin_fee, MIN_AMOUNT, MAX_AMOUNT)) return false;
    return true;
  }, [form.global]);

  const sumInvariant = useMemo(() => {
    if (!form.global) return { ok: false, expected: '0.00', actual: '0.00' };
    const pp = parseNum(form.global.position_price_usd);
    const c = parseNum(form.global.payout_cycler);
    const s = parseNum(form.global.payout_sponsor);
    const a = parseNum(form.global.payout_admin_fee);
    const expected = Number.isFinite(pp) ? pp * 3 : 0;
    const actual = Number.isFinite(c + s + a) ? c + s + a : 0;
    const ok = Number.isFinite(pp) && Number.isFinite(c) && Number.isFinite(s) && Number.isFinite(a) && Math.abs(expected - actual) < 0.005; // penny tolerance
    return { ok, expected: expected.toFixed(2), actual: actual.toFixed(2) };
  }, [form.global]);

  const tiersValid = useMemo(() => {
    if (!form.tiers || form.tiers.length === 0) return false;
    if (!form.tiers.every(tierRowValid)) return false;
    // No duplicate tier prices
    const prices = form.tiers.map((t) => toCents(t.tier_price_usd));
    if (new Set(prices).size !== prices.length) return false;
    return true;
  }, [form.tiers]);

  const isDirty = useMemo(() => {
    if (!data || !form.global) return false;
    const g0 = data.global_economics;
    const g1 = form.global;
    if (
      toMoneyStr(g0.position_price_usd) !== toMoneyStr(g1.position_price_usd) ||
      toMoneyStr(g0.payout_cycler) !== toMoneyStr(g1.payout_cycler) ||
      toMoneyStr(g0.payout_sponsor) !== toMoneyStr(g1.payout_sponsor) ||
      toMoneyStr(g0.payout_admin_fee) !== toMoneyStr(g1.payout_admin_fee)
    ) {
      return true;
    }
    if (data.tiers.length !== form.tiers.length) return true;
    for (let i = 0; i < form.tiers.length; i++) {
      const orig = data.tiers[i];
      const cur = form.tiers[i];
      const orig_we = typeof orig.withdrawal_eligible === 'boolean' ? orig.withdrawal_eligible : true;
      const cur_we = typeof cur.withdrawal_eligible === 'boolean' ? cur.withdrawal_eligible : true;
      if (
        toCents(cur.tier_price_usd) !== toCents(orig.tier_price_usd) ||
        String(cur.withdrawal_min_usd ?? '').trim() !== String(orig.withdrawal_min_usd ?? '') ||
        String(cur.withdrawal_max_usd ?? '').trim() !== String(orig.withdrawal_max_usd ?? '') ||
        parseInt(cur.total_positions, 10) !== Number(orig.total_positions) ||
        parseInt(cur.interval_seconds, 10) !==
          (Number.isFinite(orig.interval_seconds) ? Number(orig.interval_seconds) : HOURS_TO_SEC(orig.release_interval_hours)) ||
        cur_we !== orig_we
      ) {
        return true;
      }
    }
    return false;
  }, [data, form]);

  const canSave = globalValid && sumInvariant.ok && tiersValid && !saving;

  // ---- Payload builder --------------------------------------------------
  const buildPayload = () => {
    const p = {
      position_price_usd: toMoneyStr(form.global.position_price_usd),
      payout_cycler: toMoneyStr(form.global.payout_cycler),
      payout_sponsor: toMoneyStr(form.global.payout_sponsor),
      payout_admin_fee: toMoneyStr(form.global.payout_admin_fee),
      tier_ladder: form.tiers.map((t) => ({
        // D-31: sent as text so cents survive ($4.96 was parseInt'd to $4).
        tier_price_usd: String(t.tier_price_usd).trim(),
        // D-24/D-26: an empty range input clears the saved range (null); an
        // ineligible tier has no range, so its hidden inputs are ignored.
        withdrawal_min_usd: t.withdrawal_eligible === false ? null : String(t.withdrawal_min_usd ?? '').trim() || null,
        withdrawal_max_usd: t.withdrawal_eligible === false ? null : String(t.withdrawal_max_usd ?? '').trim() || null,
        total_positions: parseInt(t.total_positions, 10),
        interval_seconds: parseInt(t.interval_seconds, 10),
        // Patch v18.4 — Send strict bool. Backend validator rejects
        // anything else. If somehow undefined (defensive), default to
        // true so we never accidentally strip eligibility on save.
        withdrawal_eligible: typeof t.withdrawal_eligible === 'boolean' ? t.withdrawal_eligible : true,
      })),
    };
    return p;
  };

  // ---- Handlers ---------------------------------------------------------
  const updateGlobalField = (key, value) => {
    setForm((f) => ({
      ...f,
      global: { ...f.global, [key]: value },
    }));
  };

  const updateTierField = (idx, key, value) => {
    setForm((f) => {
      const next = f.tiers.slice();
      next[idx] = { ...next[idx], [key]: value };
      return { ...f, tiers: next };
    });
  };

  const addTierRow = () => {
    setForm((f) => {
      const highest = f.tiers.reduce((m, t) => Math.max(m, parseInt(t.tier_price_usd, 10) || 0), 0);
      const suggestPrice = highest > 0 ? highest * 2 : 1;
      return {
        ...f,
        tiers: [
          ...f.tiers,
          {
            tier_price_usd: String(suggestPrice),
            total_positions: String(suggestPrice),
            interval_seconds: String(SECONDS_PER_HOUR),
            // Patch v18.4 — New tier rows default UNCHECKED (matches
            // operator's stated intent for annual low-cost tiers).
            // Operator must explicitly check to grant withdrawal
            // eligibility on the new tier.
            withdrawal_eligible: false,
            active_pack_count: 0,
            source: 'override',
            spec_default_seconds: SECONDS_PER_HOUR,
            _orig_tier_price_usd: null,
            _new: true,
          },
        ],
      };
    });
  };

  const removeTierRow = (idx) => {
    setForm((f) => {
      if (f.tiers.length <= 1) return f;
      const target = f.tiers[idx];
      if (target?.active_pack_count > 0) {
        toast.error(
          `Cannot remove tier ${fmtUsd(target.tier_price_usd)} — it has ${target.active_pack_count} active pack(s). Wait for them to complete or use another tier.`,
        );
        return f;
      }
      return { ...f, tiers: f.tiers.filter((_, i) => i !== idx) };
    });
  };

  const flashTier = (tierPriceUsd) => {
    const k = String(tierPriceUsd);
    setFlashTiers((s) => {
      const next = new Set(s);
      next.add(k);
      return next;
    });
    setTimeout(() => {
      setFlashTiers((s) => {
        const next = new Set(s);
        next.delete(k);
        return next;
      });
    }, 1100);
  };

  const save = async () => {
    if (!canSave) {
      if (!sumInvariant.ok) {
        toast.error(
          `Sum invariant broken: cycler + sponsor + admin_fee = $${sumInvariant.actual}, but position × 3 = $${sumInvariant.expected}. Adjust before saving.`,
        );
      } else {
        toast.error('Fix highlighted validation errors before saving.');
      }
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      await updateSubpackEconomics(payload);
      // Flash the whole global card + every dirty tier to confirm save landed.
      setFlashGlobal(true);
      setTimeout(() => setFlashGlobal(false), 1100);
      form.tiers.forEach((t) => {
        flashTier(t.tier_price_usd);
      });
      toast.success('Global economics + tier ladder saved');
      await reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const resetToSpec = () => {
    if (!data) return;
    // Reset global to what the DB currently reports (which is fallback
    // to settings when no override — same effect as "reset to spec").
    // Since we don't have static "spec defaults" for the global block
    // separately, we snapshot the currently loaded values back.
    const hydrated = hydrateFromServer(data);
    if (hydrated) setForm(hydrated);
    toast.info('Form reset to last-saved values — click Save to keep changes elsewhere');
  };

  // ---- Apply to Active (legacy — interval re-pacing only) --------------
  const openApply = (tierPriceUsd) => {
    if (!data) return;
    // Save-first if dirty AND valid
    const willSaveFirst = isDirty;
    if (willSaveFirst && !canSave) {
      toast.error('Cannot apply — fix validation errors (sum invariant, bounds) before applying');
      return;
    }
    setConfirmModal({ tierPriceUsd, willSaveFirst });
    setConfirmText('');
  };

  const doApply = async () => {
    if (!confirmModal) return;
    if (confirmText !== 'APPLY') {
      toast.error('Type "APPLY" exactly to confirm');
      return;
    }
    setApplyingTier(confirmModal.tierPriceUsd ?? 'all');
    try {
      if (confirmModal.willSaveFirst) {
        if (!canSave) {
          toast.error('Cannot save — fix validation errors first');
          setApplyingTier(null);
          return;
        }
        const payload = buildPayload();
        await updateSubpackEconomics(payload);
      }
      const r = await applySubpackIntervalsToActive({
        tierPriceUsd: confirmModal.tierPriceUsd ?? null,
        confirmPhrase: 'APPLY',
      });
      const totalPacks = (r?.summary || []).reduce((acc, s) => acc + (Number(s.active_packs_updated) || 0), 0);
      const savedNote = confirmModal.willSaveFirst ? ' (saved + applied)' : '';
      toast.success(`Re-paced ${totalPacks} active Sub Pack${totalPacks === 1 ? '' : 's'}${savedNote}`);
      setConfirmModal(null);
      setConfirmText('');
      await reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Apply failed: ${msg}`);
    } finally {
      setApplyingTier(null);
    }
  };

  // ---- Renders ----------------------------------------------------------

  if (loading) {
    return (
      <div className="sc-card flex items-center justify-center py-12" data-testid="subpack-settings-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[var(--sc-text-mute)]" />
      </div>
    );
  }

  if (loadError || !data || !form.global) {
    return (
      <div className="sc-card flex flex-col items-center justify-center py-12 gap-3" data-testid="subpack-settings-error">
        <AlertTriangle className="w-6 h-6 text-[#FF7A00]" />
        <div className="text-sm text-[var(--sc-text-dim)]">Could not load Sub Pack settings.</div>
        <button type="button" onClick={() => window.location.reload()} className="sc-btn-gold" style={{ padding: '8px 16px', fontSize: 12 }}>
          Retry
        </button>
      </div>
    );
  }

  const inputBase =
    'w-full rounded-lg bg-[rgba(0,0,0,0.5)] px-3 py-2 text-sm text-[var(--sc-text)] outline-none transition-colors border focus:border-[#FFC20E]';

  return (
    <div className="space-y-6" data-testid="subpack-settings-page">
      {/* ================= Global Economics Card ================= */}
      <div className={`sc-card transition-all ${flashGlobal ? 'ring-1 ring-[#5EE6A1]' : ''}`} data-testid="global-economics-card">
        <div className="flex items-center gap-3 mb-3">
          <DollarSign className="w-5 h-5 text-[#FFC20E]" />
          <h2 className="text-lg font-display font-bold text-[#FFE08A]">Global Economics</h2>
        </div>
        <p className="text-sm text-[var(--sc-text-dim)] mb-4">
          These four values govern every dollar flow on the platform. Changes take effect on the very next heartbeat tick — no restart required.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Position Cost */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] block mb-1" htmlFor="ge-position-price">
              Position Cost (USD)
            </label>
            <input
              id="ge-position-price"
              type="number"
              inputMode="decimal"
              step="0.01"
              min={MIN_AMOUNT + 0.01}
              max={MAX_AMOUNT}
              value={form.global.position_price_usd}
              onChange={(e) => updateGlobalField('position_price_usd', e.target.value)}
              className={`${inputBase} ${inRange(form.global.position_price_usd, 0.01, MAX_AMOUNT) ? 'border-[rgba(255,182,18,0.32)]' : 'border-[#FF7A00]'}`}
              data-testid="global-position-price-input"
            />
          </div>
          {/* Cycler */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] block mb-1" htmlFor="ge-cycler">
              Cycler Payout (USD)
            </label>
            <input
              id="ge-cycler"
              type="number"
              inputMode="decimal"
              step="0.01"
              min={MIN_AMOUNT}
              max={MAX_AMOUNT}
              value={form.global.payout_cycler}
              onChange={(e) => updateGlobalField('payout_cycler', e.target.value)}
              className={`${inputBase} ${inRange(form.global.payout_cycler, 0, MAX_AMOUNT) ? 'border-[rgba(255,182,18,0.32)]' : 'border-[#FF7A00]'}`}
              data-testid="global-payout-cycler-input"
            />
          </div>
          {/* Sponsor */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] block mb-1" htmlFor="ge-sponsor">
              Sponsor Match (USD)
            </label>
            <input
              id="ge-sponsor"
              type="number"
              inputMode="decimal"
              step="0.01"
              min={MIN_AMOUNT}
              max={MAX_AMOUNT}
              value={form.global.payout_sponsor}
              onChange={(e) => updateGlobalField('payout_sponsor', e.target.value)}
              className={`${inputBase} ${inRange(form.global.payout_sponsor, 0, MAX_AMOUNT) ? 'border-[rgba(255,182,18,0.32)]' : 'border-[#FF7A00]'}`}
              data-testid="global-payout-sponsor-input"
            />
          </div>
          {/* Admin Fee */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] block mb-1" htmlFor="ge-adminfee">
              Admin Fee (USD)
            </label>
            <input
              id="ge-adminfee"
              type="number"
              inputMode="decimal"
              step="0.01"
              min={MIN_AMOUNT}
              max={MAX_AMOUNT}
              value={form.global.payout_admin_fee}
              onChange={(e) => updateGlobalField('payout_admin_fee', e.target.value)}
              className={`${inputBase} ${inRange(form.global.payout_admin_fee, 0, MAX_AMOUNT) ? 'border-[rgba(255,182,18,0.32)]' : 'border-[#FF7A00]'}`}
              data-testid="global-payout-adminfee-input"
            />
          </div>
        </div>

        {/* Sum invariant indicator */}
        <div
          className={`mt-4 rounded-lg border p-3 flex items-start gap-2 ${
            sumInvariant.ok ? 'border-[rgba(94,230,161,0.32)] bg-[rgba(94,230,161,0.06)]' : 'border-[#FF7A00] bg-[rgba(255,122,0,0.06)]'
          }`}
          data-testid="sum-invariant-indicator"
        >
          {sumInvariant.ok ? (
            <CheckCircle2 className="w-4 h-4 text-[#5EE6A1] flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-[#FF7A00] flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 text-xs">
            <div className={`font-medium mb-0.5 ${sumInvariant.ok ? 'text-[#5EE6A1]' : 'text-[#FF7A00]'}`}>
              {sumInvariant.ok ? 'Sum invariant holds' : 'Sum invariant broken'}
            </div>
            <div className="text-[var(--sc-text-dim)]">
              cycler + sponsor + admin_fee ={' '}
              <span className="sc-tabular font-medium text-[var(--sc-text)]" data-testid="sum-actual">
                {fmtUsd(sumInvariant.actual)}
              </span>{' '}
              must equal position_price × 3 ={' '}
              <span className="sc-tabular font-medium text-[var(--sc-text)]" data-testid="sum-expected">
                {fmtUsd(sumInvariant.expected)}
              </span>{' '}
              so what&apos;s collected per cycle equals what&apos;s distributed.
            </div>
          </div>
        </div>
      </div>

      {/* ================= Explainer ================= */}
      <div className="sc-card">
        <div className="flex items-center gap-3 mb-3">
          <Timer className="w-5 h-5 text-[#FFC20E]" />
          <h2 className="text-lg font-display font-bold text-[#FFE08A]">Sub Pack Tier Ladder</h2>
        </div>
        <p className="text-sm text-[var(--sc-text-dim)] mb-2">
          Configure the tier price, positions granted per purchase, and drip interval (in seconds) for every Sub Pack tier. Saving these overrides affects{' '}
          <strong>future purchases only</strong>; existing active Sub Packs keep their original cadence unless you explicitly click &ldquo;Apply to
          active&rdquo; per-tier.
        </p>
        <p className="text-xs text-[var(--sc-text-mute)]">
          Position #1 drops immediately at purchase time regardless of the interval — only positions 2..N are paced by it. Bounds: {MIN_INTERVAL_SECONDS} s to{' '}
          {MAX_INTERVAL_SECONDS.toLocaleString()} s (30 days). You may add or remove tiers; existing packs at a removed tier keep dripping normally (they carry
          their own snapshotted config).
        </p>
      </div>

      {/* ================= Tier table ================= */}
      <div className="sc-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#FFC20E]" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Tier Ladder</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]" data-testid="subpack-settings-tier-count">
              {form.tiers.length} tier{form.tiers.length === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              onClick={addTierRow}
              disabled={form.tiers.length >= 32}
              className="text-[11px] flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[rgba(255,182,18,0.32)] text-[var(--sc-text-dim)] hover:border-[#FFC20E] hover:text-[#FFE08A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="add-tier-button"
              title={form.tiers.length >= 32 ? 'Maximum 32 tiers' : 'Add a new tier row'}
            >
              <Plus className="w-3 h-3" />
              Add tier
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {form.tiers.map((t, idx) => {
            const tierKeyStr = String(t.tier_price_usd);
            const rowValid = tierRowValid(t);
            const isFlashing = flashTiers.has(tierKeyStr);
            const priceNum = parseInt(t.tier_price_usd, 10);
            const accent = TIER_BADGE_COLORS[priceNum] || DEFAULT_TIER_COLOR;
            const cardBorder = isFlashing ? 'border-[#5EE6A1]' : rowValid ? 'border-[rgba(255,182,18,0.18)]' : 'border-[#FF7A00]';
            return (
              <div
                key={idx}
                className={`rounded-xl border ${cardBorder} bg-[rgba(0,0,0,0.42)] p-4 flex flex-col md:flex-row md:items-start gap-4 transition-colors`}
                data-testid={`subpack-tier-row-${idx}`}
              >
                {/* Price badge */}
                <div className="flex items-center gap-3 md:w-[190px] flex-shrink-0">
                  <div
                    className="rounded-lg px-3 py-1.5 font-display font-bold text-base flex-shrink-0"
                    style={{
                      backgroundColor: `${accent}22`,
                      color: accent,
                      border: `1px solid ${accent}55`,
                    }}
                    data-testid={`tier-badge-${idx}`}
                  >
                    {t.tier_price_usd === '' || t.tier_price_usd == null ? '$?' : fmtUsd(t.tier_price_usd)}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTierRow(idx)}
                    disabled={form.tiers.length <= 1 || t.active_pack_count > 0}
                    className="w-8 h-8 rounded-lg border border-[rgba(255,122,0,0.32)] flex items-center justify-center text-[#FF7A00] hover:bg-[rgba(255,122,0,0.08)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title={
                      t.active_pack_count > 0
                        ? `Cannot remove — ${t.active_pack_count} active pack(s)`
                        : form.tiers.length <= 1
                          ? 'At least 1 tier must remain'
                          : 'Remove this tier'
                    }
                    data-testid={`remove-tier-${idx}`}
                    aria-label={`Remove tier ${fmtUsd(t.tier_price_usd)}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Inputs */}
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Tier Price */}
                  <div>
                    <label htmlFor={`${uid}-tier-${idx}-price`} className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] block mb-1">
                      Tier Price ($)
                    </label>
                    <input
                      id={`${uid}-tier-${idx}-price`}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min={MIN_TIER_PRICE}
                      max={MAX_TIER_PRICE}
                      value={t.tier_price_usd}
                      onChange={(e) => updateTierField(idx, 'tier_price_usd', e.target.value)}
                      className={`${inputBase} ${
                        inRange(t.tier_price_usd, MIN_TIER_PRICE, MAX_TIER_PRICE) ? 'border-[rgba(255,182,18,0.32)]' : 'border-[#FF7A00]'
                      }`}
                      data-testid={`tier-price-input-${idx}`}
                    />
                  </div>
                  {/* Positions Granted */}
                  <div>
                    <label htmlFor={`${uid}-tier-${idx}-positions`} className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] block mb-1">
                      Positions Granted
                    </label>
                    <input
                      id={`${uid}-tier-${idx}-positions`}
                      type="number"
                      inputMode="numeric"
                      step="1"
                      min={MIN_POSITIONS}
                      max={MAX_POSITIONS}
                      value={t.total_positions}
                      onChange={(e) => updateTierField(idx, 'total_positions', e.target.value)}
                      className={`${inputBase} ${
                        inRange(t.total_positions, MIN_POSITIONS, MAX_POSITIONS) ? 'border-[rgba(255,182,18,0.32)]' : 'border-[#FF7A00]'
                      }`}
                      data-testid={`tier-positions-input-${idx}`}
                    />
                  </div>
                  {/* Interval Seconds */}
                  <div>
                    <label htmlFor={`${uid}-tier-${idx}-seconds`} className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] block mb-1">
                      Interval (seconds)
                    </label>
                    <input
                      id={`${uid}-tier-${idx}-seconds`}
                      type="number"
                      inputMode="numeric"
                      step="1"
                      min={MIN_INTERVAL_SECONDS}
                      max={MAX_INTERVAL_SECONDS}
                      value={t.interval_seconds}
                      onChange={(e) => updateTierField(idx, 'interval_seconds', e.target.value)}
                      className={`${inputBase} ${
                        inRange(t.interval_seconds, MIN_INTERVAL_SECONDS, MAX_INTERVAL_SECONDS) ? 'border-[rgba(255,182,18,0.32)]' : 'border-[#FF7A00]'
                      }`}
                      data-testid={`tier-seconds-input-${idx}`}
                    />
                    <div className="text-[10px] text-[var(--sc-text-mute)] mt-1">
                      ≈ {fmtSecondsHuman(t.interval_seconds)} ({SEC_TO_MIN(t.interval_seconds).toLocaleString()} min)
                    </div>
                  </div>
                </div>

                {/* Patch v18.4 — Withdrawal-Eligibility toggle. Placed
                    between the input grid and the "Active packs" column
                    so it reads as a per-tier meta setting. Bright when
                    ON, dim when OFF, matching how the member-facing
                    dropdown will render this tier. */}
                <div className="md:min-w-[180px] flex md:flex-col md:items-start gap-2 md:pt-[22px]">
                  <label
                    className={`inline-flex items-start gap-2 cursor-pointer select-none rounded-lg px-2.5 py-2 border transition-colors ${
                      t.withdrawal_eligible
                        ? 'border-[rgba(94,230,161,0.42)] bg-[rgba(94,230,161,0.06)]'
                        : 'border-[rgba(255,182,18,0.18)] bg-[rgba(0,0,0,0.32)]'
                    }`}
                    data-testid={`tier-eligibility-label-${idx}`}
                    title={
                      t.withdrawal_eligible
                        ? 'Purchases at this tier grant withdrawal eligibility'
                        : 'Purchases at this tier do NOT grant withdrawal eligibility'
                    }
                  >
                    <input
                      type="checkbox"
                      checked={!!t.withdrawal_eligible}
                      onChange={(e) => updateTierField(idx, 'withdrawal_eligible', e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-[rgba(255,182,18,0.32)] bg-[rgba(0,0,0,0.5)] accent-[#5EE6A1] cursor-pointer flex-shrink-0"
                      data-testid={`tier-eligibility-checkbox-${idx}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        {t.withdrawal_eligible ? (
                          <Unlock className="w-3 h-3 text-[#5EE6A1] flex-shrink-0" />
                        ) : (
                          <Lock className="w-3 h-3 text-[var(--sc-text-mute)] flex-shrink-0" />
                        )}
                        <span
                          className={`text-[10.5px] uppercase tracking-[0.16em] font-medium whitespace-nowrap ${
                            t.withdrawal_eligible ? 'text-[#5EE6A1]' : 'text-[var(--sc-text-mute)]'
                          }`}
                        >
                          Withdrawal Eligible
                        </span>
                      </div>
                      <div className="text-[9.5px] text-[var(--sc-text-mute)] mt-0.5 leading-tight">
                        {t.withdrawal_eligible ? 'New purchases unlock withdraw' : "New purchases WON'T unlock withdraw"}
                      </div>
                    </div>
                  </label>
                  {t.withdrawal_eligible && (
                    <div className="flex items-center gap-1.5" data-testid={`tier-range-${idx}`}>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0.01"
                        placeholder="Min $"
                        value={t.withdrawal_min_usd ?? ''}
                        onChange={(e) => updateTierField(idx, 'withdrawal_min_usd', e.target.value)}
                        className={`${inputBase} w-[84px] ${rangeValid(t) ? 'border-[rgba(255,182,18,0.32)]' : 'border-[#FF7A00]'}`}
                        title="Smallest withdrawal a pack of this tier allows (empty = no range)"
                        data-testid={`tier-range-min-${idx}`}
                      />
                      <span className="text-[var(--sc-text-mute)] text-xs">–</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0.01"
                        placeholder="Max $"
                        value={t.withdrawal_max_usd ?? ''}
                        onChange={(e) => updateTierField(idx, 'withdrawal_max_usd', e.target.value)}
                        className={`${inputBase} w-[84px] ${rangeValid(t) ? 'border-[rgba(255,182,18,0.32)]' : 'border-[#FF7A00]'}`}
                        title="Largest withdrawal a pack of this tier allows (empty = no range)"
                        data-testid={`tier-range-max-${idx}`}
                      />
                    </div>
                  )}
                </div>

                {/* Active packs + Apply */}
                <div className="flex md:flex-col md:items-end justify-between md:justify-start gap-2 md:min-w-[140px]">
                  <div className="md:text-right">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] block">Active Packs</span>
                    <span className="text-base font-medium text-[var(--sc-text)] sc-tabular" data-testid={`tier-active-count-${idx}`}>
                      {t.active_pack_count}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => openApply(String(t.tier_price_usd).trim())}
                    disabled={t.active_pack_count === 0 || applyingTier !== null || Boolean(t._new)}
                    className="text-[11px] flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[rgba(255,182,18,0.32)] text-[var(--sc-text-dim)] hover:border-[#FFC20E] hover:text-[#FFE08A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title={
                      t._new
                        ? 'Save first, then apply'
                        : t.active_pack_count === 0
                          ? 'No active packs at this tier'
                          : `Re-pace ${t.active_pack_count} active pack(s)`
                    }
                    data-testid={`tier-apply-${idx}`}
                  >
                    <Repeat className="w-3 h-3" />
                    Apply to active
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Master controls */}
        <div className="mt-5 pt-5 border-t border-[rgba(255,182,18,0.12)] flex flex-col md:flex-row md:items-center gap-3 justify-between">
          <div className="flex items-center gap-2 text-[11px] text-[var(--sc-text-mute)]">
            <Zap className="w-3.5 h-3.5" />
            Changes to Global Economics + tier ladder take effect on the very next heartbeat tick. Use &ldquo;Apply to active&rdquo; per-tier to re-pace
            existing packs.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={resetToSpec}
              className="text-[11px] flex items-center gap-1 px-3 py-2 rounded-lg border border-[rgba(255,182,18,0.32)] text-[var(--sc-text-dim)] hover:border-[#FFC20E] hover:text-[#FFE08A] transition-colors"
              data-testid="subpack-settings-reset-spec"
            >
              <RotateCcw className="w-3 h-3" />
              Revert unsaved
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!isDirty || !canSave}
              className="sc-btn-gold"
              style={{ padding: '10px 18px', fontSize: 12 }}
              data-testid="subpack-settings-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save All
            </button>
            <button
              type="button"
              onClick={() => openApply(null)}
              disabled={form.tiers.every((t) => t.active_pack_count === 0) || applyingTier !== null}
              className="text-[11px] flex items-center gap-1 px-3 py-2 rounded-lg border border-[#FF7A00] text-[#FF7A00] hover:bg-[rgba(255,122,0,0.08)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="subpack-settings-apply-all"
            >
              <Repeat className="w-3 h-3" />
              {isDirty ? 'Save & apply to ALL' : 'Apply to ALL active'}
            </button>
          </div>
        </div>
      </div>

      {/* ================= Confirmation modal ================= */}
      {confirmModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="subpack-apply-confirm-modal">
          <div className="sc-card max-w-md w-full">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-[#FF7A00]" />
              <h3 className="text-base font-display font-bold text-[#FFE08A]">
                {confirmModal.willSaveFirst ? 'Confirm Save + Retro-Apply' : 'Confirm Retro-Apply'}
              </h3>
            </div>
            <p className="text-sm text-[var(--sc-text-dim)] mb-3">
              {confirmModal.willSaveFirst ? (
                <>
                  This will <strong>save your Global Economics + tier ladder</strong> then re-pace{' '}
                  {confirmModal.tierPriceUsd === null ? (
                    <>
                      <strong>every active Sub Pack across every tier</strong> so their next drop adopts the saved interval.
                    </>
                  ) : (
                    <>
                      every active Sub Pack at the <strong>{fmtUsd(confirmModal.tierPriceUsd)}</strong> tier so their next drop adopts the saved interval.
                    </>
                  )}{' '}
                  Historical positions are not affected.
                </>
              ) : confirmModal.tierPriceUsd === null ? (
                <>
                  This will re-pace <strong>every active Sub Pack across every tier</strong> to use the currently-saved intervals. Historical positions are not
                  affected.
                </>
              ) : (
                <>
                  This will re-pace every active Sub Pack at the <strong>{fmtUsd(confirmModal.tierPriceUsd)}</strong> tier so their next drop adopts the saved
                  interval. Historical positions are not affected.
                </>
              )}
            </p>
            <p className="text-xs text-[var(--sc-text-mute)] mb-4">
              Type <span className="font-mono text-[#FFC20E]">APPLY</span> to dispatch:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="APPLY"
              className="w-full rounded-lg bg-[rgba(0,0,0,0.5)] px-3 py-2 text-sm text-[var(--sc-text)] outline-none border border-[rgba(255,182,18,0.32)] focus:border-[#FFC20E] mb-4"
              data-testid="subpack-apply-confirm-input"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setConfirmModal(null);
                  setConfirmText('');
                }}
                disabled={applyingTier !== null}
                className="text-[12px] px-4 py-2 rounded-lg border border-[rgba(255,182,18,0.32)] text-[var(--sc-text-dim)] hover:border-[#FFC20E] hover:text-[#FFE08A] disabled:opacity-40 transition-colors"
                data-testid="subpack-apply-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doApply}
                disabled={confirmText !== 'APPLY' || applyingTier !== null}
                className="text-[12px] flex items-center gap-1 px-4 py-2 rounded-lg bg-[#FF7A00] text-black font-medium hover:bg-[#FF9500] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="subpack-apply-confirm"
              >
                {applyingTier !== null ? <Loader2 className="w-3 h-3 animate-spin" /> : <Repeat className="w-3 h-3" />}
                {confirmModal.willSaveFirst ? 'Save & apply now' : 'Apply now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubpackSettingsSubpage;
